import React, { useEffect, useMemo, useState } from 'react';
import { ViewSitesPanel } from './SuperAdminPanel';
import { SafetyFormPanel } from './SafetyFormPanel';
import { SubmittedFormsPanel } from './SubmittedFormsPanel';
import { HazardsAndControlPanel } from './HazardsAndControlPanel';
import { SUBMITTED_AND_HAZARDS_SECTION, SUBMITTED_FORMS_VIEW, HAZARDS_CONTROL_VIEW } from '../config/dashboardViews';
import {
  CONFIGURE_PROFILE_VIEW,
  CollapsibleAccountInfo,
  ConfigureProfilePanel,
} from './ConfigureProfilePanel';
import { LogoutButton } from './LogoutButton';
import { MobileActionBar, useIsDesktop } from './MobileActionBar';
import { DashboardCardSection } from './DashboardCardSection';
import { UserProfileProvider } from '../context/UserProfileContext';
import { apiUrl } from '../config/api';
import {
  FORM_FREQUENCY,
  getSafetyFormCardsForRole,
  getSafetyFormCardsByFrequency,
  isSafetyFormView,
  parseSafetyFormIndex,
} from '../config/safetyForms';
import { SafetyFormFrequencyNav } from './SafetyFormFrequencyNav';
import { ScrollableRightPane } from './ScrollEdgeButtons';
import { CollapsibleSidebarNav } from './CollapsibleSidebarNav';
import { refreshCurrentUser } from '../utils/refreshCurrentUser';
import { getStoredUser } from '../utils/auth';
import { isProjectManager, isSuperVisor, platformAdminRoleLabel } from '../utils/roles';

const VIEW_SITES = 'view-sites';

function DesktopLogoutRow() {
  const isDesktop = useIsDesktop();
  if (!isDesktop) return null;
  return (
    <div className="mb-2 flex items-center justify-end gap-2">
      <LogoutButton />
    </div>
  );
}

function renderPanel(activeView, onBack, projectName, showSites, allowAssign) {
  if (isSafetyFormView(activeView)) {
    const formIndex = parseSafetyFormIndex(activeView);
    return <SafetyFormPanel formIndex={formIndex} projectName={projectName} onBack={onBack} />;
  }
  if (activeView === SUBMITTED_FORMS_VIEW) {
    return <SubmittedFormsPanel onBack={onBack} ownOnly />;
  }
  if (activeView === HAZARDS_CONTROL_VIEW) {
    return <HazardsAndControlPanel onBack={onBack} />;
  }
  if (activeView === VIEW_SITES && showSites) {
    return (
      <ViewSitesPanel
        key={VIEW_SITES}
        onBack={onBack}
        readOnly
        allowAssign={allowAssign}
        title="Manage Sites/Projects"
        description={
          allowAssign
            ? 'View your assigned sites/projects and assign an Assistant Supervisor.'
            : 'View the sites/projects assigned to you.'
        }
      />
    );
  }
  return null;
}

function findActiveCard(activeView, sections, formCards) {
  const card = sections.flatMap((s) => s.cards).find((c) => c.id === activeView);
  if (card) return card;
  return formCards.find((c) => c.id === activeView);
}

export function SafetyRoleDashboard({ user: initialUser, showSites = false, allowAssign = false }) {
  const [activeView, setActiveView] = useState('home');
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [formFrequency, setFormFrequency] = useState(FORM_FREQUENCY.DAILY);
  const [formStatus, setFormStatus] = useState('pending');
  const [formFilterKind, setFormFilterKind] = useState('status'); // 'status' | 'frequency'
  const [rightFormSearch, setRightFormSearch] = useState('');
  const [user, setUser] = useState(initialUser || getStoredUser());

  useEffect(() => {
    const email = user?.email;
    if (!email) return undefined;
    let cancelled = false;
    (async () => {
      const refreshed = await refreshCurrentUser(email);
      if (!cancelled && refreshed) setUser(refreshed);
    })();
    return () => {
      cancelled = true;
    };
  }, [user?.email]);

  const companyName = user?.company_name || user?.project_name || 'HINDBO';
  const companyLogoUrl = user?.company_logo_url ? apiUrl(user.company_logo_url) : null;
  const roleLabel = platformAdminRoleLabel(user?.role);
  const dashboardTitle = isProjectManager(user?.role)
    ? 'Project Manager Dashboard'
    : user?.role === 'assistant_super_visor'
      ? 'Assistant Supervisor Dashboard'
      : 'Supervisor Dashboard';

  const formCards = useMemo(
    () => getSafetyFormCardsForRole(user?.role, companyName),
    [companyName, user?.role]
  );
  const filteredFormCards = useMemo(() => {
    if (formFilterKind === 'status') {
      if (formStatus === 'all') return formCards;
      if (formStatus === 'overdue') return [];
      // Pending — rules later; show all for now so the list isn't empty.
      if (formStatus === 'pending') return formCards;
      return formCards;
    }
    return getSafetyFormCardsByFrequency(companyName, formFrequency, user?.role);
  }, [companyName, formCards, formFrequency, formStatus, formFilterKind, user?.role]);
  const formSection = useMemo(() => {
    const q = rightFormSearch.trim().toLowerCase();
    const cards = !q
      ? filteredFormCards
      : filteredFormCards.filter((card) => {
          const haystack = [card.title, card.description, card.frequencyNote]
            .filter(Boolean)
            .join(' ')
            .toLowerCase();
          return haystack.includes(q);
        });
    return { label: 'Safety Forms', cards };
  }, [filteredFormCards, rightFormSearch]);

  const adminSections = useMemo(() => {
    if (!showSites) return [];
    return [
      {
        label: 'Sites/Projects',
        cards: [
          {
            id: VIEW_SITES,
            title: 'Manage Sites/Projects',
            description: allowAssign
              ? 'View sites and assign an Assistant Supervisor.'
              : 'View your assigned sites/projects.',
            icon: '📍',
          },
        ],
      },
    ];
  }, [showSites, allowAssign]);

  const homeSections = useMemo(
    () => [...adminSections, SUBMITTED_AND_HAZARDS_SECTION, formSection],
    [adminSections, formSection]
  );

  const extraSections = useMemo(
    () => [...adminSections, SUBMITTED_AND_HAZARDS_SECTION],
    [adminSections]
  );

  const activeCard = findActiveCard(activeView, extraSections, formCards);
  const isFormView = isSafetyFormView(activeView);
  const isProfileView = activeView === CONFIGURE_PROFILE_VIEW;
  const showHeaderToolbar =
    activeView === 'home' ||
    isProfileView ||
    (activeView !== 'home' && activeCard && !isFormView);
  const showFrequencyTabs = true;
  const handleNavigate = (view) => {
    setActiveView(view);
    setMobileNavOpen(false);
  };

  const handleFormStatusChange = (nextStatus) => {
    setFormFilterKind('status');
    setFormStatus(nextStatus);
    setActiveView('home');
  };

  const handleFrequencyChange = (nextFrequency) => {
    setFormFilterKind('frequency');
    setFormFrequency(nextFrequency);
    setActiveView('home');
  };

  const handleOpenCard = (cardId) => {
    handleNavigate(cardId);
  };

  const handleSelectForm = (card) => {
    handleNavigate(card.id);
  };

  const statusEmptyText =
    formFilterKind === 'status' && formStatus === 'overdue'
      ? 'No overdue forms yet.'
      : formFilterKind === 'frequency' && formFrequency === FORM_FREQUENCY.YEARLY
        ? 'No yearly forms in this list.'
        : formFilterKind === 'status' && formStatus === 'all'
          ? 'No forms found.'
          : 'No forms in this category.';

  return (
    <UserProfileProvider email={user?.email}>
    <div className="relative h-screen bg-black text-white flex overflow-hidden">
      <MobileActionBar menuOpen={mobileNavOpen} onOpenMenu={() => setMobileNavOpen(true)}>
        {showHeaderToolbar ? <LogoutButton /> : null}
      </MobileActionBar>
      {mobileNavOpen ? (
        <button
          type="button"
          className="fixed inset-0 z-40 bg-black/70 lg:hidden"
          onClick={() => setMobileNavOpen(false)}
          aria-label="Close navigation"
        />
      ) : null}
      <aside
        className={`fixed inset-y-0 left-0 z-50 w-[min(20rem,calc(100vw-2rem))] bg-gray-950 border-r border-gray-800 p-4 flex flex-col shrink-0 overflow-hidden transition-transform duration-200 lg:static lg:z-auto lg:w-80 lg:translate-x-0 lg:p-6 ${
          mobileNavOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <button
          type="button"
          onClick={() => setMobileNavOpen(false)}
          className="absolute right-3 top-3 inline-flex h-10 w-10 items-center justify-center rounded-md border border-gray-700 text-xl text-gray-300 hover:text-white lg:hidden"
          aria-label="Close navigation"
        >
          ×
        </button>
        <div className="mb-6 shrink-0">
          {companyLogoUrl ? (
            <div className="mb-4 w-full h-16 rounded-md border border-gray-700 bg-gray-900 overflow-hidden flex items-center justify-center">
              <img
                src={companyLogoUrl}
                alt={`${companyName} logo`}
                className="max-h-full max-w-full object-contain"
              />
            </div>
          ) : null}
          <h1 className="text-xl font-bold truncate">{companyName}</h1>
          {user ? (
            <CollapsibleAccountInfo
              email={user.email}
              roleLabel={roleLabel}
              roleClassName="text-sky-200/90"
              profileActive={isProfileView}
              onConfigureProfile={() => handleNavigate(CONFIGURE_PROFILE_VIEW)}
            />
          ) : null}
        </div>
        <button
          type="button"
          onClick={() => handleNavigate('home')}
          className={`w-full text-left px-4 py-2 rounded-md text-sm font-medium transition-colors mb-4 shrink-0 ${
            activeView === 'home'
              ? 'bg-gray-800 text-white'
              : 'text-gray-400 hover:bg-gray-900 hover:text-white'
          }`}
        >
          Dashboard
        </button>

        {extraSections.map((section) => (
          <CollapsibleSidebarNav
            key={section.label}
            label={section.label}
            cards={section.cards}
            activeView={activeView}
            onSelect={handleNavigate}
          />
        ))}

        <SafetyFormFrequencyNav
          cards={filteredFormCards}
          frequency={formFrequency}
          onFrequencyChange={handleFrequencyChange}
          activeFormId={activeView}
          onSelectForm={handleSelectForm}
          showFormStatusTabs
          formStatus={formStatus}
          onFormStatusChange={handleFormStatusChange}
          showFrequencyTabs={showFrequencyTabs}
          activeFilterKind={formFilterKind}
          emptyText={statusEmptyText}
          filtersAlwaysOpen={isSuperVisor(user?.role) || isProjectManager(user?.role)}
        />
      </aside>

      {isFormView ? (
        <main className="relative flex-1 min-w-0 min-h-0 flex flex-col overflow-hidden px-3 pb-3 pt-16 sm:px-4 lg:px-8 lg:pb-8 lg:pt-3">
          <div className="mx-auto w-full max-w-5xl flex flex-col flex-1 min-h-0">
            {showHeaderToolbar && <DesktopLogoutRow />}
            {activeView !== 'home' && !isProfileView && activeCard
              ? renderPanel(activeView, () => setActiveView('home'), companyName, showSites, allowAssign)
              : null}
          </div>
        </main>
      ) : (
        <ScrollableRightPane
          className="flex-1 min-w-0"
          scrollClassName="px-2 sm:px-4 pt-16 pb-3 lg:py-3"
        >
          <div className="mx-auto w-full max-w-none">
            {showHeaderToolbar && <DesktopLogoutRow />}

            {activeView === 'home' && (
              <>
                <h2 className="text-2xl font-semibold mb-2">{dashboardTitle}</h2>
                <p className="text-gray-400 text-sm mb-8">
                  {formFilterKind === 'status' && formStatus === 'all'
                    ? `${formCards.length} safety forms — open one to fill it out.`
                    : formFilterKind === 'status' && formStatus === 'overdue'
                      ? 'No overdue forms yet.'
                      : formFilterKind === 'status' && formStatus === 'pending'
                        ? `${filteredFormCards.length} pending form${
                            filteredFormCards.length === 1 ? '' : 's'
                          } — open one to fill it out.`
                        : `${filteredFormCards.length} ${formFrequency.replace('_', ' ')} form${
                            filteredFormCards.length === 1 ? '' : 's'
                          } — open one to fill it out.`}
                </p>
                {homeSections.map((section) => (
                  <DashboardCardSection
                    key={section.label}
                    label={section.label}
                    cards={section.cards}
                    onOpenCard={handleOpenCard}
                    accent={section.label === 'Sites/Projects' ? 'sky' : 'amber'}
                    {...(section.label === 'Safety Forms'
                      ? {
                          searchValue: rightFormSearch,
                          onSearchChange: setRightFormSearch,
                        }
                      : {})}
                  />
                ))}
              </>
            )}

            {isProfileView && (
              <div className="border border-gray-800 rounded-lg bg-gray-950/70 p-4 md:p-5 min-h-full">
                <ConfigureProfilePanel onBack={() => setActiveView('home')} />
              </div>
            )}

            {activeView !== 'home' && !isProfileView && activeCard && (
              <div className="border border-gray-800 rounded-lg bg-gray-950/70 p-2.5 md:p-3 min-h-full">
                {renderPanel(activeView, () => setActiveView('home'), companyName, showSites, allowAssign)}
              </div>
            )}
          </div>
        </ScrollableRightPane>
      )}
    </div>
    </UserProfileProvider>
  );
}

export function SuperVisorDashboard({ user }) {
  return <SafetyRoleDashboard user={user} showSites allowAssign />;
}

export function AssistantSuperVisorDashboard({ user }) {
  return <SafetyRoleDashboard user={user} showSites allowAssign={false} />;
}

export function ProjectManagerDashboard({ user }) {
  return <SafetyRoleDashboard user={user} showSites allowAssign={false} />;
}
